import {useCallback, useSyncExternalStore} from 'react';

import type {IndexedDevices, MediaDeviceInfoLike} from '@pexip/media-control';
import {compareDevices, reverseDeviceId} from '@pexip/media-control';
import type {Signal} from '@pexip/signal';
import type {
    GeneratedMediaSignals,
    MediaSignals,
    MediaTrack,
} from '@pexip/media';
import {EMPTY_USER_MEDIA_STATUS} from '@pexip/media';

import {UserMediaContext} from '../contexts/userMedia';
import type {Subscribe} from '../types';
import {
    createChangeOfMediaDevicesSubscription,
    createChangeOfTrackSubscription,
    createRequestedMediaSubscription,
} from '../utils/userMedia';

import {useAssertedContext} from './useAssertContext';
import {useDeviceStatusInfo} from './useDeviceStatusInfo';

export const useUserMediaContext = () => useAssertedContext(UserMediaContext);

interface SelectDeviceChangeHandlerParams {
    devices: IndexedDevices;
    currentInput: MediaDeviceInfoLike | undefined;
    requestMedia: (device: MediaDeviceInfoLike | undefined) => void;
}
export const useSelectDeviceChangeHandler = ({
    devices,
    currentInput,
    requestMedia,
}: SelectDeviceChangeHandlerParams) =>
    useCallback(
        (id: string) => {
            if (!id) {
                return requestMedia(undefined);
            }
            const selected = reverseDeviceId(id);
            const [selectedDevice] = devices.get(selected);
            if (
                currentInput &&
                selectedDevice &&
                compareDevices(currentInput)(selectedDevice)
            ) {
                return;
            }
            requestMedia(selectedDevice);
        },
        [currentInput, devices, requestMedia],
    );

interface MediaParams<K extends keyof MediaSignals> {
    muteAudio: (muted: boolean, persist?: boolean) => void;
    muteVideo: (muted: boolean, persist?: boolean) => void;
    preMuteAudio?: (muted: boolean) => void;
    preMuteVideo?: (muted: boolean) => void;
    signals: GeneratedMediaSignals<K>;
}

export const createUserMediaHooks = ({
    muteAudio,
    muteVideo,
    preMuteAudio,
    preMuteVideo,
    signals,
}: MediaParams<
    | 'onAddTrack'
    | 'onDevicesChanged'
    | 'onMediaChanged'
    | 'onMediaTrackMuted'
    | 'onMediaTrackResumed'
    | 'onMediaTrackStopped'
    | 'onMediaTrackSuspended'
    | 'onRemoveTrack'
    | 'onStatusChanged'
    | 'onUpdatingAudio'
    | 'onUpdatingVideo'
>) => {
    const changeOfMediaDevices =
        createChangeOfMediaDevicesSubscription(signals);
    const changeOfAudioTrack = createChangeOfTrackSubscription(
        signals,
        'audio',
    );
    const changeOfVideoTrack = createChangeOfTrackSubscription(
        signals,
        'video',
    );
    const changeOfAudioRequest = createRequestedMediaSubscription(
        signals,
        'audio',
    );
    const changeOfVideoRequest = createRequestedMediaSubscription(
        signals,
        'video',
    );
    const subscribeTrackStateChange =
        (
            kind: MediaTrack['kind'],
            ...mediaTrackSignals: Signal<MediaTrack>[]
        ): Subscribe =>
        onChange => {
            const handleTrackChanges = (track: MediaTrack) => {
                if (track.kind === kind) {
                    onChange();
                }
            };
            const unsubscribes = [
                ...mediaTrackSignals.map(signal =>
                    signal.add(handleTrackChanges),
                ),
                createChangeOfTrackSubscription(
                    signals,
                    kind === 'audioinput' ? 'audio' : 'video',
                )(onChange),
            ];
            return () => {
                for (const unsubscribe of unsubscribes) {
                    unsubscribe();
                }
                unsubscribes.length = 0;
            };
        };
    const subscribeAudioMuted = subscribeTrackStateChange(
        'audioinput',
        signals.onMediaTrackMuted,
        signals.onMediaTrackStopped,
    );
    const subscribeAudioSuspended = subscribeTrackStateChange(
        'audioinput',
        signals.onMediaTrackSuspended,
        signals.onMediaTrackResumed,
    );

    const subscribeVideoMuted = subscribeTrackStateChange(
        'videoinput',
        signals.onMediaTrackMuted,
        signals.onMediaTrackStopped,
    );
    const subscribeVideoSuspended = subscribeTrackStateChange(
        'videoinput',
        signals.onMediaTrackSuspended,
        signals.onMediaTrackResumed,
    );

    const useAudioInput = (subscribe: Subscribe = changeOfAudioTrack) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(
            subscribe,
            () => userMedia.media?.audioInput,
        );
    };
    const useVideoInput = (subscribe: Subscribe = changeOfVideoTrack) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(
            subscribe,
            () => userMedia.media?.videoInput,
        );
    };

    const useDevices = (
        subscribe: Subscribe = signals.onDevicesChanged.add,
    ) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(subscribe, () => userMedia.devices);
    };

    const useMediaStream = (
        subscribe: Subscribe = signals.onMediaChanged.add,
    ) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(subscribe, () => userMedia.media?.stream);
    };

    const useMediaStatus = (
        subscribe: Subscribe = signals.onStatusChanged.add,
    ) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(
            subscribe,
            () => userMedia.media?.status ?? EMPTY_USER_MEDIA_STATUS,
        );
    };

    const useAudioMuteState = (subscribe: Subscribe = subscribeAudioMuted) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(subscribe, () => {
            const tracks = userMedia.media?.getAudioTracks();
            if (!tracks || tracks.length === 0) {
                return true;
            }
            return tracks.some(track => {
                const muted = track.stopped || track.muted;
                return muted === true || muted === undefined;
            });
        });
    };
    const useAudioSuspended = (
        subscribe: Subscribe = subscribeAudioSuspended,
    ) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(subscribe, () => {
            const tracks = userMedia.media?.getAudioTracks();
            if (!tracks || tracks.length === 0) {
                return false;
            }
            return tracks.some(track => track.suspended);
        });
    };
    const useVideoMuteState = (subscribe: Subscribe = subscribeVideoMuted) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(subscribe, () => {
            const tracks = userMedia.media?.getVideoTracks();
            if (!tracks || tracks.length === 0) {
                return true;
            }
            return tracks.some(track => {
                const muted = track.stopped || track.muted;
                return muted === true || muted === undefined;
            });
        });
    };
    const useVideoSuspended = (
        subscribe: Subscribe = subscribeVideoSuspended,
    ) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(subscribe, () => {
            const tracks = userMedia.media?.getVideoTracks();
            if (!tracks || tracks.length === 0) {
                return false;
            }
            return tracks.some(track => track.suspended);
        });
    };

    const useMuteAudio = (setState = muteAudio, preMute = preMuteAudio) => {
        const userMedia = useUserMediaContext();
        return useCallback(
            (muted: boolean, persist?: boolean) => {
                if (userMedia.media?.audioMuted === undefined) {
                    return;
                }
                preMute?.(muted);
                userMedia.media.muteAudio(muted);
                setState(userMedia.media.audioMuted, persist);
            },
            [userMedia, setState, preMute],
        );
    };

    const useMuteVideo = (setState = muteVideo, preMute = preMuteVideo) => {
        const userMedia = useUserMediaContext();
        return useCallback(
            (muted: boolean, persist?: boolean) => {
                if (userMedia.media?.videoMuted === undefined) {
                    return;
                }
                preMute?.(muted);
                userMedia.media.muteVideo(muted);
                setState(userMedia.media.videoMuted, persist);
            },
            [userMedia, setState, preMute],
        );
    };

    const useToggleAudioMuted = (
        setState = muteAudio,
        preMute = preMuteAudio,
    ) => {
        const userMedia = useUserMediaContext();
        const mute = useMuteAudio(setState, preMute);
        return useCallback(
            (persist?: boolean) => {
                if (userMedia.media?.audioMuted === undefined) {
                    return;
                }
                mute(!userMedia.media.audioMuted, persist);
            },
            [userMedia, mute],
        );
    };

    const useToggleVideoMuted = (
        setState = muteVideo,
        preMute = preMuteVideo,
    ) => {
        const userMedia = useUserMediaContext();
        const mute = useMuteVideo(setState, preMute);
        return useCallback(
            (persist?: boolean) => {
                if (userMedia.media?.videoMuted === undefined) {
                    return;
                }
                mute(!userMedia.media.videoMuted, persist);
            },
            [userMedia, mute],
        );
    };

    const useHaveRequestedAudio = (
        subscribe: Subscribe = changeOfAudioRequest,
    ) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(
            subscribe,
            () => userMedia.media?.requestedAudio() ?? false,
        );
    };

    const useHaveRequestedVideo = (
        subscribe: Subscribe = changeOfVideoRequest,
    ) => {
        const userMedia = useUserMediaContext();
        return useSyncExternalStore(
            subscribe,
            () => userMedia.media?.requestedVideo() ?? false,
        );
    };

    const useSourceAudioTrack = (subscribe: Subscribe = changeOfAudioTrack) => {
        const userMedia = useUserMediaContext();
        const getState = () =>
            userMedia.media?.getAudioTracks().at(0)?.source.track;
        return useSyncExternalStore(subscribe, getState);
    };

    const useSourceVideoTrack = (subscribe: Subscribe = changeOfVideoTrack) => {
        const userMedia = useUserMediaContext();
        const getState = () =>
            userMedia.media?.getVideoTracks().at(0)?.source.track;
        return useSyncExternalStore(subscribe, getState);
    };

    const useAudioInputUnavailable = (
        subscribe: Subscribe = changeOfMediaDevices,
    ) => {
        const userMedia = useUserMediaContext();
        const getState = () =>
            userMedia.media?.isAudioInputUnavailable() ?? false;
        return useSyncExternalStore(subscribe, getState);
    };

    const useVideoInputUnavailable = (
        subscribe: Subscribe = changeOfMediaDevices,
    ) => {
        const userMedia = useUserMediaContext();
        const getState = () =>
            userMedia.media?.isVideoInputUnavailable() ?? false;
        return useSyncExternalStore(subscribe, getState);
    };

    const useHaveRequestedMedia = () => ({
        audio: useHaveRequestedAudio(),
        video: useHaveRequestedVideo(),
    });

    const useInputDeviceStatusInfo = () => {
        const status = useMediaStatus();
        const requested = useHaveRequestedMedia();
        return useDeviceStatusInfo(status, requested);
    };

    return {
        useAudioInput,
        useAudioInputUnavailable,
        useAudioMuteState,
        useAudioSuspended,
        useDevices,
        useHaveRequestedAudio,
        useHaveRequestedMedia,
        useHaveRequestedVideo,
        useInputDeviceStatusInfo,
        useMediaStatus,
        useMediaStream,
        useMuteAudio,
        useMuteVideo,
        useSourceAudioTrack,
        useSourceVideoTrack,
        useToggleAudioMuted,
        useToggleVideoMuted,
        useVideoInput,
        useVideoInputUnavailable,
        useVideoMuteState,
        useVideoSuspended,
    };
};
